import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, Sparkles, Plus } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { GameState, CollectibleObject, UserItemInstance } from '../types';
import { useTranslation } from '../contexts/TranslationContext';

// Type pour un groupe d'instances similaires
interface InstanceGroup {
  baseElementId: number;
  appliedEffects: string[];
  count: number;
  instances: UserItemInstance[];
  element: CollectibleObject;
}

interface TransformationScreenProps {
  gameState: GameState;
  allObjects: CollectibleObject[];
  onBack: () => void;
  onTransform: (targetInstanceId: number, specialInstanceId: number) => void;
}

const TransformationScreen: React.FC<TransformationScreenProps> = ({
  gameState,
  allObjects,
  onBack,
  onTransform
}) => {
  const [draggedGroup, setDraggedGroup] = useState<InstanceGroup | null>(null);
  const [slotGroup, setSlotGroup] = useState<InstanceGroup | null>(null);
  const [specialSlotGroup, setSpecialSlotGroup] = useState<InstanceGroup | null>(null);
  const [showSparkle, setShowSparkle] = useState(false);
  const { t } = useTranslation();

  // Touch drag state
  const [touchDragGroup, setTouchDragGroup] = useState<InstanceGroup | null>(null);
  const [touchDragPos, setTouchDragPos] = useState<{ x: number; y: number } | null>(null);
  const normalSlotRef = useRef<HTMLDivElement>(null);
  const specialSlotRef = useRef<HTMLDivElement>(null);

  // Grouper les instances par (baseElementId + effets identiques)
  const { normalGroups, specialGroups } = useMemo(() => {
    const activeInstances = (gameState.userInstances || []).filter((inst: UserItemInstance) => !inst.archived);

    // Map pour regrouper : clé = "baseElementId|effect1,effect2,..."
    const groupMap = new Map<string, InstanceGroup>();

    activeInstances.forEach((instance: UserItemInstance) => {
      const element = allObjects.find(obj => obj.id === instance.baseElementId);
      if (!element) return;

      const effectsKey = [...(instance.appliedEffects || [])].sort().join(',');
      const groupKey = `${instance.baseElementId}|${effectsKey}`;

      if (groupMap.has(groupKey)) {
        const group = groupMap.get(groupKey)!;
        group.count++;
        group.instances.push(instance);
      } else {
        groupMap.set(groupKey, {
          baseElementId: instance.baseElementId,
          appliedEffects: instance.appliedEffects || [],
          count: 1,
          instances: [instance],
          element
        });
      }
    });

    const allGroups = Array.from(groupMap.values());
    return {
      normalGroups: allGroups.filter(g => !g.element.isSpecial),
      specialGroups: allGroups.filter(g => g.element.isSpecial)
    };
  }, [gameState.userInstances, allObjects]);

  const handleDragStart = (e: React.DragEvent, group: InstanceGroup) => {
    setDraggedGroup(group);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnSlot = (e: React.DragEvent, slotType: 'normal' | 'special') => {
    e.preventDefault();
    if (!draggedGroup) return;

    if (slotType === 'normal' && !draggedGroup.element.isSpecial) {
      setSlotGroup(draggedGroup);
    } else if (slotType === 'special' && draggedGroup.element.isSpecial) {
      setSpecialSlotGroup(draggedGroup);
    }

    setDraggedGroup(null);
  };

  // Touch handlers pour mobile
  const handleTouchStart = (group: InstanceGroup, e: React.TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    setTouchDragGroup(group);
    setTouchDragPos({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!touchDragGroup) return;
    e.preventDefault();
    const touch = e.touches[0];
    setTouchDragPos({ x: touch.clientX, y: touch.clientY });
  }, [touchDragGroup]);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (!touchDragGroup || !touchDragPos) {
      setTouchDragGroup(null);
      setTouchDragPos(null);
      return;
    }

    // Vérifier si on a lâché sur un slot
    const checkSlotHit = (slotRef: React.RefObject<HTMLDivElement>, slotType: 'normal' | 'special') => {
      if (!slotRef.current) return false;
      const rect = slotRef.current.getBoundingClientRect();
      if (
        touchDragPos.x >= rect.left &&
        touchDragPos.x <= rect.right &&
        touchDragPos.y >= rect.top &&
        touchDragPos.y <= rect.bottom
      ) {
        if (slotType === 'normal' && !touchDragGroup.element.isSpecial) {
          setSlotGroup(touchDragGroup);
          return true;
        } else if (slotType === 'special' && touchDragGroup.element.isSpecial) {
          setSpecialSlotGroup(touchDragGroup);
          return true;
        }
      }
      return false;
    };

    checkSlotHit(normalSlotRef, 'normal');
    checkSlotHit(specialSlotRef, 'special');

    setTouchDragGroup(null);
    setTouchDragPos(null);
  }, [touchDragGroup, touchDragPos]);

  // Attacher les événements touch au document
  useEffect(() => {
    if (touchDragGroup) {
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      document.addEventListener('touchcancel', handleTouchEnd);
      return () => {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchcancel', handleTouchEnd);
      };
    }
  }, [touchDragGroup, handleTouchMove, handleTouchEnd]);

  const handleTransform = () => {
    if (!slotGroup || !specialSlotGroup) return;

    // Prendre la première instance de chaque groupe
    const targetInstance = slotGroup.instances[0];
    const specialInstance = specialSlotGroup.instances[0];

    if (!targetInstance || !specialInstance) {
      alert('❌ ' + t("transformation.erreur_instances_non_trouvees", "Erreur: instances non trouvées"));
      return;
    }

    // Animation scintillante
    setShowSparkle(true);
    setTimeout(() => setShowSparkle(false), 1000);

    // Appeler la fonction de transformation avec les instanceIds
    onTransform(targetInstance.id, specialInstance.id);

    // Reset des slots
    setTimeout(() => {
      setSlotGroup(null);
      setSpecialSlotGroup(null);
    }, 1000);
  };

  const clearSlot = (slotType: 'normal' | 'special') => {
    if (slotType === 'normal') {
      setSlotGroup(null);
    } else {
      setSpecialSlotGroup(null);
    }
  };

  // Helper pour afficher les effets
  const getEffectBadge = (effects: string[]) => {
    if (effects.length === 0) return null;
    return (
      <div className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 text-xs px-1 rounded-full flex items-center">
        <Sparkles size={10} className="mr-0.5" />
        {effects.length}
      </div>
    );
  };

  const isNative = Capacitor.isNativePlatform();

  return (
    <div className="h-screen-adaptive bg-gradient-to-b from-purple-100 to-pink-100 relative overflow-hidden">
      {/* Header */}
      <div className={`bg-white shadow-sm ${isNative ? 'safe-area-top pt-2' : ''}`}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <ChevronLeft size={20} />
              {t("transformation.back", "Back")}
            </button>
            <h1 className="text-xl font-bold text-gray-900 font-title flex items-center gap-2">
              <Sparkles size={20} />
              {t("transformation.transformation", "Transformation")}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Instructions */}
        <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-lg p-4 mb-6">
          <p className="text-gray-700 text-center">
            {t("transformation.glissez_un_objet_normal_dans_le_premier_slot_puis", "Glissez un objet normal dans le premier slot, puis un objet spécial dans le second slot pour créer une version scintillante !")}
          </p>
        </div>

        {/* Zone de transformation */}
        <div>
          {/* Slots de transformation */}
          <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-xl p-6 mb-6">
            {/* Slots */}
            <div className="flex items-center justify-center gap-6 mb-6">
              {/* Slot objet normal */}
              <div
                ref={normalSlotRef}
                className={`relative w-24 h-24 border-4 border-dashed rounded-xl flex items-center justify-center transition-all ${
                  touchDragGroup && !touchDragGroup.element.isSpecial
                    ? 'border-purple-500 bg-purple-100 scale-110'
                    : 'border-purple-300 bg-purple-50 hover:border-purple-400'
                }`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDropOnSlot(e, 'normal')}
              >
                {slotGroup ? (
                  <div className="relative">
                    <img
                      src={slotGroup.element.emoji}
                      alt={slotGroup.element.name}
                      className={`w-16 h-16 object-contain ${slotGroup.appliedEffects.length > 0 ? 'drop-shadow-[0_0_8px_rgba(255,215,0,0.8)]' : ''}`}
                    />
                    {getEffectBadge(slotGroup.appliedEffects)}
                    <button
                      onClick={() => clearSlot('normal')}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <Plus className="text-purple-400" size={32} />
                )}
              </div>

              {/* Plus symbole */}
              <Plus className="text-purple-400" size={24} />

              {/* Slot objet spécial */}
              <div
                ref={specialSlotRef}
                className={`relative w-24 h-24 border-4 border-dashed rounded-xl flex items-center justify-center transition-all ${
                  touchDragGroup && touchDragGroup.element.isSpecial
                    ? 'border-yellow-500 bg-yellow-100 scale-110'
                    : 'border-yellow-300 bg-yellow-50 hover:border-yellow-400'
                }`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDropOnSlot(e, 'special')}
              >
                {specialSlotGroup ? (
                  <div className="relative">
                    <img
                      src={specialSlotGroup.element.emoji}
                      alt={specialSlotGroup.element.name}
                      className="w-16 h-16 object-contain animate-pulse"
                    />
                    <button
                      onClick={() => clearSlot('special')}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <Sparkles className="text-yellow-400" size={32} />
                )}
              </div>
            </div>

            {/* Bouton de transformation */}
            <div className="flex justify-center">
              <button
                onClick={handleTransform}
                disabled={!slotGroup || !specialSlotGroup}
                className={`px-8 py-3 rounded-lg font-bold transition-all ${
                  slotGroup && specialSlotGroup
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {showSparkle ? (
                  <Sparkles className="animate-spin" size={24} />
                ) : (
                  t("transformation.transformer", "Transformer") + ' ✨'
                )}
              </button>
            </div>
          </div>

          {/* Liste des objets disponibles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Objets normaux (groupés par type + effets) */}
            <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-xl p-4">
              <h3 className="font-bold text-purple-800 mb-4 flex items-center gap-2">
                <div className="w-4 h-4 bg-purple-400 rounded"></div>
                {t("transformation.objets_normaux", "Objets Normaux")}
              </h3>
              {normalGroups.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">{t("transformation.aucun_objet_dans_la_collection", "Aucun objet dans la collection")}</p>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  {normalGroups.map((group) => (
                    <div
                      key={`${group.baseElementId}-${group.appliedEffects.join(',')}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, group)}
                      onTouchStart={(e) => handleTouchStart(group, e)}
                      className={`relative p-2 rounded-lg cursor-move transition-colors border-2 select-none ${
                        group.appliedEffects.length > 0
                          ? 'bg-gradient-to-br from-purple-50 to-yellow-50 border-yellow-300 hover:border-yellow-400'
                          : 'bg-purple-50 border-purple-200 hover:border-purple-300 hover:bg-purple-100'
                      } ${touchDragGroup === group ? 'opacity-50 scale-95' : ''}`}
                      style={{ touchAction: 'none', WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
                    >
                      <img
                        src={group.element.emoji}
                        alt={group.element.name}
                        className={`w-12 h-12 object-contain mx-auto ${
                          group.appliedEffects.length > 0 ? 'drop-shadow-[0_0_6px_rgba(255,215,0,0.7)]' : ''
                        }`}
                        style={{ WebkitTouchCallout: 'none', pointerEvents: 'none' }}
                      />
                      {getEffectBadge(group.appliedEffects)}
                      {group.count > 1 && (
                        <div className="absolute bottom-0 right-0 bg-purple-600 text-white text-xs px-1.5 rounded-tl">
                          x{group.count}
                        </div>
                      )}
                      <p className="text-xs text-center mt-1 text-purple-700 truncate">{group.element.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Objets spéciaux */}
            <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-xl p-4">
              <h3 className="font-bold text-yellow-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-yellow-600" />
                {t("transformation.objets_speciaux", "Objets Spéciaux")}
              </h3>
              {specialGroups.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">{t("transformation.aucun_objet_special", "Aucun objet spécial")}</p>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  {specialGroups.map((group) => (
                    <div
                      key={`${group.baseElementId}-${group.appliedEffects.join(',')}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, group)}
                      onTouchStart={(e) => handleTouchStart(group, e)}
                      className={`relative p-2 bg-yellow-50 rounded-lg cursor-move hover:bg-yellow-100 transition-colors border-2 border-yellow-200 hover:border-yellow-300 select-none ${
                        touchDragGroup === group ? 'opacity-50 scale-95' : ''
                      }`}
                      style={{ touchAction: 'none', WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
                    >
                      <img
                        src={group.element.emoji}
                        alt={group.element.name}
                        className="w-12 h-12 object-contain mx-auto animate-pulse"
                        style={{ WebkitTouchCallout: 'none', pointerEvents: 'none' }}
                      />
                      {group.count > 1 && (
                        <div className="absolute bottom-0 right-0 bg-yellow-600 text-white text-xs px-1.5 rounded-tl">
                          x{group.count}
                        </div>
                      )}
                      <p className="text-xs text-center mt-1 text-yellow-700 truncate">{group.element.name}</p>
                      <Sparkles className="absolute top-0 right-0 w-3 h-3 text-yellow-500" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Effet de particules quand transformation */}
      {showSparkle && (
        <div className="absolute inset-0 pointer-events-none z-50">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 opacity-20 animate-pulse"></div>
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-ping"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 1000}ms`,
                animationDuration: '1500ms'
              }}
            >
              <Sparkles className="text-yellow-400 w-4 h-4" />
            </div>
          ))}
        </div>
      )}

      {/* Floating drag preview for touch */}
      {touchDragGroup && touchDragPos && (
        <div
          className="fixed pointer-events-none z-50"
          style={{
            left: touchDragPos.x - 32,
            top: touchDragPos.y - 32,
            transform: 'scale(1.2)',
          }}
        >
          <div className={`p-2 rounded-lg shadow-lg ${
            touchDragGroup.element.isSpecial
              ? 'bg-yellow-100 border-2 border-yellow-400'
              : 'bg-purple-100 border-2 border-purple-400'
          }`}>
            <img
              src={touchDragGroup.element.emoji}
              alt={touchDragGroup.element.name}
              className={`w-12 h-12 object-contain ${
                touchDragGroup.element.isSpecial ? 'animate-pulse' : ''
              } ${touchDragGroup.appliedEffects.length > 0 ? 'drop-shadow-[0_0_6px_rgba(255,215,0,0.7)]' : ''}`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TransformationScreen;